// PlayerLimitsFields — set a new player's MAX BET limits AT CREATION, in the same
// dumb-broker 4-card shape as the player-detail editor (BPG-5, 2026-08-05):
//   🎰 Casino  = ONE max bet for every non-API game (BPG-6 single 'casino' bucket)
//   🏏 ⚽ 🎾   = one "Max bet" per game, with an Advanced expander for the detailed
//               caps (match-odds / bookmaker / session-fancy / per-match exposure)
// Points; blank = no cap, 0 = blocked. These ARE the upline's to set.

import React, { useState } from 'react';

export type SportCaps = {
  maxBet?: number | '';
  maxStakeMatchOdds?: number | ''; maxStakeBookmaker?: number | '';
  maxStakeFancy?: number | ''; maxMatchExposure?: number | '';
};
export type PlayerLimits = {
  casinoMaxBet?: number | '';
  cricket?: SportCaps; football?: SportCaps; tennis?: SportCaps;
};

const SPORTS: { key: 'cricket' | 'football' | 'tennis'; label: string }[] = [
  { key: 'cricket', label: '🏏 Cricket' }, { key: 'football', label: '⚽ Football' }, { key: 'tennis', label: '🎾 Tennis' },
];
const inputCls =
  'w-full rounded-lg pl-6 pr-2.5 py-1.5 text-[13px] text-right tabular-nums font-mono ' +
  'bg-white dark:bg-neutral-900/50 border border-neutral-200 dark:border-white/10 ' +
  'focus:outline-none focus:ring-2 focus:ring-emerald-400/40 focus:border-emerald-400 disabled:opacity-50';

function Rupee({ value, onChange, disabled }: { value: number | '' | undefined; onChange: (v: number | '') => void; disabled?: boolean }) {
  return (
    <div className="relative">
      <span className="absolute left-2 top-1/2 -translate-y-1/2 text-[12px] text-neutral-400 pointer-events-none">₹</span>
      <input type="number" min={0} step={100} disabled={disabled} value={value ?? ''} placeholder="no limit"
        onChange={(e) => onChange(e.target.value === '' ? '' : Math.max(0, Number(e.target.value)))} className={inputCls} />
    </div>
  );
}

export default function PlayerLimitsFields({
  value, onChange, disabled,
}: {
  value: PlayerLimits; onChange: (patch: PlayerLimits) => void; disabled?: boolean;
}) {
  const [adv, setAdv] = useState<Record<string, boolean>>({});
  const setSport = (sport: 'cricket' | 'football' | 'tennis', field: keyof SportCaps, v: number | '') =>
    onChange({ [sport]: { ...(value[sport] || {}), [field]: v } });

  return (
    <div className="rounded-xl border border-neutral-200 dark:border-white/10 bg-neutral-50/60 dark:bg-white/[0.02] p-3 space-y-3">
      <div className="flex items-center gap-2">
        <span className="text-[13px] font-bold text-neutral-800 dark:text-neutral-100">Max bet</span>
        <span className="text-[10.5px] text-neutral-400">yours to set · blank = no cap · 0 = blocked</span>
      </div>

      {/* 🎰 Casino */}
      <div className="rounded-lg border border-neutral-200 dark:border-white/10 p-2.5">
        <span className="block text-[12px] font-semibold text-neutral-700 dark:text-neutral-200 mb-1">🎰 Casino</span>
        <Rupee value={value.casinoMaxBet} onChange={(v) => onChange({ casinoMaxBet: v })} disabled={disabled} />
        <span className="block text-[9.5px] text-neutral-400 mt-0.5">all games except cricket · football · tennis</span>
      </div>

      {/* per game */}
      {SPORTS.map(({ key, label }) => {
        const c = value[key] || {};
        return (
          <div key={key} className="rounded-lg border border-neutral-200 dark:border-white/10 p-2.5 space-y-2">
            <div className="flex items-center justify-between gap-2">
              <span className="text-[12px] font-semibold text-neutral-700 dark:text-neutral-200">{label}</span>
              <button type="button" disabled={disabled} onClick={() => setAdv((a) => ({ ...a, [key]: !a[key] }))}
                className="text-[10.5px] font-semibold px-2 py-0.5 rounded-full border border-emerald-300 text-emerald-700 dark:border-emerald-500/40 dark:text-emerald-300 hover:bg-emerald-500/10 disabled:opacity-50">
                {adv[key] ? '↩ Simple' : 'Advanced →'}
              </button>
            </div>
            {!adv[key] ? (
              <>
                <Rupee value={c.maxBet} onChange={(v) => setSport(key, 'maxBet', v)} disabled={disabled} />
                <span className="block text-[9.5px] text-neutral-400">one max bet for {key} · tap Advanced for per-market caps</span>
              </>
            ) : (
              <>
                <div className="grid grid-cols-2 gap-2">
                  {([['maxStakeMatchOdds', 'Match odds'], ['maxStakeBookmaker', 'Bookmaker'], ['maxStakeFancy', 'Session/Fancy'], ['maxMatchExposure', 'Per-match exposure']] as const).map(([f, lbl]) => (
                    <label key={f} className="text-[10.5px] text-neutral-600 dark:text-neutral-300">{lbl}
                      <div className="mt-0.5"><Rupee value={c[f]} onChange={(v) => setSport(key, f, v)} disabled={disabled} /></div>
                    </label>
                  ))}
                </div>
                <span className="block text-[9.5px] text-amber-600 dark:text-amber-400">these detailed caps apply to {key} only</span>
              </>
            )}
          </div>
        );
      })}

      <p className="text-[10px] text-neutral-400 leading-snug">Rebate &amp; platform fees are admin-set for the whole downline — not yours to set here.</p>
    </div>
  );
}

// Build the API `limits` object: 🎰 Casino = one casino cap for all non-API games; each
// sport uses its Advanced caps if any are set, else the single "Max bet" for the
// three stake markets. Omits empty sports/fields.
export function limitsPayload(v: PlayerLimits): Record<string, unknown> | undefined {
  const int = (x: any) => (x === '' || x == null || !Number.isFinite(Number(x)) ? undefined : Math.floor(Number(x)));
  const out: Record<string, unknown> = {};
  const casino = int(v.casinoMaxBet);
  if (casino !== undefined) { out.casinoMaxBet = casino; }
  const perSport: Record<string, unknown> = {};
  for (const { key } of SPORTS) {
    const c = v[key]; if (!c) continue;
    const adv = { maxStakeMatchOdds: int(c.maxStakeMatchOdds), maxStakeBookmaker: int(c.maxStakeBookmaker), maxStakeFancy: int(c.maxStakeFancy), maxMatchExposure: int(c.maxMatchExposure) };
    const anyAdv = Object.values(adv).some((x) => x !== undefined);
    const mb = int(c.maxBet);
    let caps: Record<string, number> | null = null;
    if (anyAdv) caps = Object.fromEntries(Object.entries(adv).filter(([, x]) => x !== undefined)) as Record<string, number>;
    else if (mb !== undefined) caps = { maxStakeMatchOdds: mb, maxStakeBookmaker: mb, maxStakeFancy: mb };
    if (caps && Object.keys(caps).length) perSport[key] = caps;
  }
  if (Object.keys(perSport).length) out.perSport = perSport;
  return Object.keys(out).length ? out : undefined;
}
